import * as queryString from './QueryString';
import { getRouteQuery } from './Route';
import requestLib, { authHttpRequest } from './Request';
import { setCommonAmalgam } from './Amalgam';
import {
  getShowcaseId,
  setCommonAmalgamForGroupCatalogue,
  setGroupRouteSlugs,
  getGroupRouteSlugs,
  setShowcaseId
} from './GroupCatalogueAmalgam';
import { parseResellerTabIndex, setOtpAuthValue } from './Reseller';

let amalgamObject = null;
let getAmalgamPromise = null;
let getResellerOrdersAmalgamPromise = null;
let getResellerOrderDetailsPromise = null;

export const ORDER_ROW_HEIGHT = 115;

const ORDER_PAGE_DATA = {
  lastOrderId: undefined,
  lastDateUpdated: undefined,
  totalOrders: -1,
  nextPageExists: true
};

const setResellerOrderAmalagam = (orders) => {
  if (!amalgamObject) {
    amalgamObject = {};
  }

  amalgamObject.orders = orders;
};

const setResellerOrdersPaginationData = ({ nextOrderId, nextDateUpdated, totalOrders } = {}) => {
  if (typeof totalOrders === 'number') {
    ORDER_PAGE_DATA.totalOrders = totalOrders;
  }
  ORDER_PAGE_DATA.lastDateUpdated = nextDateUpdated;
  ORDER_PAGE_DATA.lastOrderId = nextOrderId;

  if (!(nextDateUpdated && nextOrderId)) {
    ORDER_PAGE_DATA.nextPageExists = false;
  }
};

const setCommonAmalgamFromResellerView = (amalgam) => {
  setCommonAmalgam({
    companyId: amalgam.companyId,
    companyName: amalgam.companyName,
    companyAddress: amalgam.companyAddress,
    companyDetails: amalgam.companyDetails,
    companyDomain: amalgam.companyDomain,
    companyDomains: amalgam.companyDomains,
    currencyCode: amalgam.currencyCode,
    currencySymbol: amalgam.currencySymbol,
    logoExists: amalgam.logoExists,
    logoVersion: amalgam.logoVersion,
    currencyOptions: amalgam.currencyOptions,
    isBlockedCompany: amalgam.isBlockedCompany,
    localeNumberFormats: amalgam.localeNumberFormats,
    paymentPlan: amalgam.paymentPlan,
    extendedTrialStart: amalgam.extendedTrialStart,
    i18n: amalgam.i18n,
    companyExperiments: amalgam.companyExperiments,
    visitorId: amalgam.visitorId,
    userPhone: amalgam.user
      ? amalgam.user.phone
        ? amalgam.user.phone
        : amalgam.user.userPhone
      : amalgam.userPhone,
    userName: amalgam.user
      ? amalgam.user.name
        ? amalgam.user.name
        : amalgam.user.userName
      : amalgam.userName,
    userEmail: amalgam.user
      ? amalgam.user.email
        ? amalgam.user.email
        : amalgam.user.userEmail
      : amalgam.userEmail,
    pureWhitelabel: amalgam.pureWhitelabel,
    facebookPixelIds: amalgam.facebookPixelIds,
    googleAnalyticsIds: amalgam.companyExperiments.googleAnalytics,
    isPaid: amalgam.isPaid,
    planType: amalgam.planType,
    theme: amalgam.theme || amalgam.catalogueTheme,
    isHomePage: amalgam.isHomePage,
    isCustomPage: amalgam.isCustomPage,
    hasCustomFrontPage: amalgam.hasCustomFrontPage,
    menuItems: amalgam.menuItems,
    customFooter: amalgam.customFooter,
    customFooterTheme: amalgam.customFooterTheme,
    customFonts: amalgam.customFonts,
    unitDetails: amalgam.unitDetails
  });

  setCommonAmalgamForGroupCatalogue(amalgam);
  setGroupRouteSlugs(amalgamObject);
  setOtpAuthValue(true);
  // Currently, amalgam is only loaded for the catalogues, can be made as per the requested tab
};

// On load the amalgam object will be present in the window object, use it to initalize the required variables
if (window.amalgamObject && window.amalgamObject.isResellerView) {
  amalgamObject = window.amalgamObject;
  // eslint-disable-next-line no-undef
  metaDataObject = window.metaDataObject;

  setCommonAmalgamFromResellerView(amalgamObject);
}

// Handle reload on inner views
if (window.amalgamObject && window.amalgamObject.resellerViewData) {
  setGroupRouteSlugs(window.amalgamObject.resellerViewData);
  setShowcaseId(window.amalgamObject.resellerViewData.groupShowcaseId);
}

export const getResellerViewAmalgam = ({ tabIndex, orderId, companySlug, randomSlug }) => {
  const { referrer, referrerVal } = getRouteQuery();

  if (amalgamObject) {
    return Promise.resolve(amalgamObject);
  }

  if (getAmalgamPromise) {
    return getAmalgamPromise;
  }

  getAmalgamPromise = new Promise((resolve, reject) => {
    const query = queryString.stringify({
      companySlug,
      randomSlug,
      referrer,
      referrerVal,
      tabIndex: parseResellerTabIndex({ tabIndex }),
      orderId
    });

    requestLib({
      method: 'GET',
      url: `/reseller-view-amalgam?${query}`
    })
      .then((response) => {
        amalgamObject = response.amalgam;
        // eslint-disable-next-line no-undef
        metaDataObject = response.metaData;
        resolve();
        getAmalgamPromise = null;

        setCommonAmalgamFromResellerView(amalgamObject);
      })
      .catch(() => {
        reject();
        getAmalgamPromise = null;
      });
  });

  return getAmalgamPromise;
};

export const isResellerViewAmalgamFetched = () => !!amalgamObject;

export const isResellerOrderAmalgamFetched = () => {
  if (!amalgamObject) {
    return false;
  }

  return !!amalgamObject.orders;
};

export const getResellerAmalagamOrders = () => (amalgamObject || {}).orders || [];

export const setResellerOrderDetails = (orderDetails) => {
  if (!amalgamObject || !orderDetails) {
    return;
  }
  if (!amalgamObject.orderDetails) {
    amalgamObject.orderDetails = {};
  }
  amalgamObject.orderDetails[orderDetails.id] = orderDetails;
};

export const getResellerOrderDetails = ({ orderId }) => {
  if (!amalgamObject || !amalgamObject.orderDetails || !amalgamObject.orderDetails[orderId]) {
    return null;
  }
  return amalgamObject.orderDetails[orderId];
};

export const getResellerOrdersList = () => {
  if (getResellerOrdersAmalgamPromise) {
    return getResellerOrdersAmalgamPromise;
  }

  if (getAmalgamPromise) {
    return getAmalgamPromise;
  }

  getResellerOrdersAmalgamPromise = new Promise((resolve, reject) => {
    const queryObject = {
      groupShowcaseId: getShowcaseId()
    };
    if (ORDER_PAGE_DATA.lastOrderId) {
      queryObject.lastOrderId = ORDER_PAGE_DATA.lastOrderId;
    }

    if (ORDER_PAGE_DATA.lastDateUpdated) {
      queryObject.lastDateUpdated = ORDER_PAGE_DATA.lastDateUpdated;
    }

    const query = queryString.stringify(queryObject);

    authHttpRequest({
      method: 'GET',
      url: `/api/reseller/order-list?${query}`
    })
      .then(({ ordersList, nextOrderId, nextDateUpdated, totalOrders } = {}) => {
        resolve();
        getResellerOrdersAmalgamPromise = null;
        setResellerOrderAmalagam(ordersList);
        setResellerOrdersPaginationData({ nextOrderId, nextDateUpdated, totalOrders });
      })
      .catch(() => {
        reject();
        getResellerOrdersAmalgamPromise = null;
      });
  });

  return getResellerOrdersAmalgamPromise;
};

export const getResellerOrderData = ({ orderId }) => {
  if (getResellerOrderDetailsPromise) {
    return getResellerOrderDetailsPromise;
  }

  const query = queryString.stringify({
    groupShowcaseId: getShowcaseId()
  });

  getResellerOrderDetailsPromise = new Promise((resolve, reject) => {
    authHttpRequest({
      method: 'GET',
      url: `/api/reseller/order-details/${orderId}?${query}`
    })
      .then((orderDetails) => {
        setResellerOrderDetails(orderDetails);
        resolve();
        getResellerOrderDetailsPromise = null;
      })
      .catch(() => {
        reject();
        getResellerOrderDetailsPromise = null;
      });
  });

  return getResellerOrderDetailsPromise;
};

export const getResellerRoute = (tabIndex) => {
  tabIndex = parseResellerTabIndex({ tabIndex });
  const { companySlug, randomSlug } = getGroupRouteSlugs();
  return `/r/${companySlug}/${randomSlug}?tabIndex=${tabIndex}`;
};

export const getPaymentStatus = () => amalgamObject.paymentStatus || '';
